import axios from "axios";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

let refreshing = null;
export const refreshSession = () => {
  refreshing = refreshing || axios.post(`${API}/auth/refresh`).finally(() => { refreshing = null; });
  return refreshing;
};

axios.interceptors.response.use(
  (r) => r,
  async (err) => {
    const cfg = err.config;
    const expired = err.response?.status === 401 && err.response?.data?.detail === "Not authenticated";
    if (expired && cfg && !cfg._retry && !cfg.url.includes("/auth/refresh")) {
      cfg._retry = true;
      try {
        await refreshSession();
        return axios(cfg);
      } catch {}
    }
    return Promise.reject(err);
  }
);

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const listChats = () => axios.get(`${API}/chats`).then((r) => r.data);
export const getChat = (id) => axios.get(`${API}/chats/${id}`).then((r) => r.data);
export const deleteChat = (id) => axios.delete(`${API}/chats/${id}`);
export const deleteAccount = (body) => axios.delete(`${API}/auth/account`, { data: body }).then((r) => r.data);
export const shareTurn = (chatId, index) => axios.post(`${API}/chats/${chatId}/turns/${index}/share`).then((r) => r.data);
export const debateCard = (chatId, index, fmt) =>
  axios.get(`${API}/chats/${chatId}/turns/${index}/card`, { params: { fmt }, responseType: "blob" }).then((r) => r.data);
export const getShare = (id) => axios.get(`${API}/share/${id}`).then((r) => r.data);
export const getPlans = () => axios.get(`${API}/billing/plans`).then((r) => r.data);
export const startCheckout = (interval = "month") =>
  axios.post(`${API}/billing/checkout`, { callback_url: `${window.location.origin}/payment/callback`, interval }).then((r) => r.data);
export const verifyPayment = (ref) => axios.get(`${API}/billing/verify/${ref}`).then((r) => r.data);
export const cancelPlan = () => axios.post(`${API}/billing/cancel`).then((r) => r.data);
export const billingHistory = () => axios.get(`${API}/billing/history`).then((r) => r.data);
export const payfastCheckout = (plan, classId, interval = "month") => axios.post(`${API}/billing/payfast/checkout`, { plan, class_id: classId, interval }).then((r) => r.data);
export const payfastStatus = (ref) => axios.get(`${API}/billing/payfast/status/${ref}`).then((r) => r.data);

export function goToCheckout(result) {
  if (result.authorization_url) {
    window.location.href = result.authorization_url;
    return;
  }
  const form = document.createElement("form");
  form.method = "POST";
  form.action = result.action;
  Object.entries(result.fields).forEach(([name, value]) => {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = value;
    form.appendChild(input);
  });
  document.body.appendChild(form);
  form.submit();
}
export const enableKids = (pin, age) => axios.post(`${API}/kids/enable`, { pin, age }).then((r) => r.data);
export const kidsApi = {
  setAge: (age, pin) => axios.post(`${API}/kids/age`, { age, pin }).then((r) => r.data),
  intro: () => axios.get(`${API}/kids/intro`).then((r) => r.data),
  introAudio: (scene) => axios.post(`${API}/kids/intro/audio/${scene}`).then((r) => r.data),
  introComplete: (code) => axios.post(`${API}/kids/intro/complete`, { code }).then((r) => r.data),
  listen: (blob, ext) => {
    const f = new FormData();
    f.append("audio", blob, `clip.${ext}`);
    return axios.post(`${API}/kids/listen`, f).then((r) => r.data);
  },
  speak: (text) => axios.post(`${API}/kids/listen/speak`, { text }).then((r) => r.data),
  practice: (blob, ext, listenId) => {
    const f = new FormData();
    f.append("audio", blob, `clip.${ext}`);
    f.append("listen_id", listenId);
    return axios.post(`${API}/kids/listen/practice`, f).then((r) => r.data);
  },
  stars: () => axios.get(`${API}/kids/stars`).then((r) => r.data),
};
export const changePassword = (body) => axios.post(`${API}/auth/change-password`, body).then((r) => r.data);
export const changeEmail = (body) => axios.post(`${API}/auth/change-email`, body).then((r) => r.data);
export const testersApi = {
  join: (body) => axios.post(`${API}/testers`, body).then((r) => r.data),
  list: () => axios.get(`${API}/admin/testers`).then((r) => r.data),
  mark: (id, added) => axios.patch(`${API}/admin/testers/${id}`, { added }).then((r) => r.data),
  remove: (id) => axios.delete(`${API}/admin/testers/${id}`).then((r) => r.data),
};
export const disableKids = (body) => axios.post(`${API}/kids/disable`, body).then((r) => r.data);
export const explainWord = (word, context) => axios.post(`${API}/kids/explain`, { word, context }).then((r) => r.data);
export const readAloud = (chatId, index, part) => axios.post(`${API}/tts`, { chat_id: chatId, turn_index: index, part }).then((r) => r.data);
export const words = {
  list: () => axios.get(`${API}/kids/words`).then((r) => r.data),
  save: (w) => axios.post(`${API}/kids/words`, w).then((r) => r.data),
  remove: (word) => axios.delete(`${API}/kids/words/${encodeURIComponent(word)}`),
  listen: (word) => axios.post(`${API}/tts/word`, { word }).then((r) => r.data),
  quiz: (score, total) => axios.post(`${API}/kids/quiz`, { score, total }).then((r) => r.data),
};
export const forgotPassword = (email) => axios.post(`${API}/auth/forgot-password`, { email }).then((r) => r.data);
export const resetPassword = (token, password) => axios.post(`${API}/auth/reset-password`, { token, password }).then((r) => r.data);
export const school = {
  guardianCode: (code, password, kind) => axios.post(`${API}/school/guardian-code`, { code, password, kind }).then((r) => r.data),
  setAge: (classId, studentId, age) => axios.patch(`${API}/school/classes/${classId}/students/${studentId}/age`, { age }).then((r) => r.data),
  info: () => axios.get(`${API}/school/info`).then((r) => r.data),
  list: () => axios.get(`${API}/school/classes`).then((r) => r.data),
  create: (name, kind) => axios.post(`${API}/school/classes`, { name, kind }).then((r) => r.data),
  get: (id) => axios.get(`${API}/school/classes/${id}`).then((r) => r.data),
  update: (id, body) => axios.patch(`${API}/school/classes/${id}`, body).then((r) => r.data),
  addStudents: (id, names) => axios.post(`${API}/school/classes/${id}/students`, { names }).then((r) => r.data),
  resetStudent: (id, sid) => axios.post(`${API}/school/classes/${id}/students/${sid}/reset-password`).then((r) => r.data),
  removeStudent: (id, sid) => axios.delete(`${API}/school/classes/${id}/students/${sid}`),
  checkout: (id) => axios.post(`${API}/school/classes/${id}/checkout`, { callback_url: `${window.location.origin}/payment/callback` }).then((r) => r.data),
  cancel: (id) => axios.post(`${API}/school/classes/${id}/cancel`).then((r) => r.data),
  progress: (id) => axios.get(`${API}/school/classes/${id}/progress`).then((r) => r.data),
};

const postAsk = (body) =>
  fetch(`${API}/ask`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

export async function askStream(question, chatId, prefs, onEvent) {
  const body = { question, chat_id: chatId, models: prefs.models, merger: prefs.merger, debate: !!prefs.debate, more: !!prefs.more };
  let res = await postAsk(body);
  if (res.status === 401) {
    await refreshSession();
    res = await postAsk(body);
  }
  if (!res.ok) {
    const detail = await res.json().then((d) => d.detail).catch(() => null);
    throw new Error(detail || `Request failed (${res.status})`);
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split("\n\n");
    buffer = parts.pop();
    for (const p of parts) {
      const line = p.trim();
      if (line.startsWith("data:")) onEvent(JSON.parse(line.slice(5).trim()));
    }
  }
}
